// Report service
import { Monitor } from "../types/Screen";
import { Media } from "../types/Media";
import { Report, ReportListResponse } from "../types/Report";
import { ReportRequest } from "../types/ReportRequest";
import { Holiday } from "../types/Holiday";
import {
	mockGetScreens,
	mockGetMedia,
	mockGenerateReport,
	mockGetAllReports,
	mockDownloadReport,
	mockGetHolidays,
} from "./mockData";
import { apiClient } from "./apiClient";

// Check if we should use mock API
const USE_MOCK_API =
	process.env.REACT_APP_USE_MOCK_API === "true" ||
	!process.env.REACT_APP_API_BASE_URL;

const parseHolidayString = (value: string): Holiday[] => {
	const trimmed = value.trim();
	if (!trimmed) return [];

	try {
		const parsed = JSON.parse(trimmed);
		return normalizeHolidaysResponse(parsed);
	} catch {
		return trimmed
			.split(/\r?\n|,/)
			.map((line) => line.trim())
			.filter(Boolean)
			.map((line) => {
				const match = line.match(/^(\d{4}-\d{2}-\d{2})\s*[-:]\s*(.+)$/);
				return match
					? { date: match[1], name: match[2].trim() }
					: { name: line };
			});
	}
};

const normalizeHolidayObject = (value: Record<string, unknown>): Holiday => {
	const date =
		typeof value.date === "string"
			? value.date
			: typeof value.holiday_date === "string"
				? value.holiday_date
				: undefined;
	const nameCandidates = [
		value.name,
		value.localName,
		value.holiday,
		value.description,
		value.title,
	];
	const name = nameCandidates.find(
		(candidate): candidate is string =>
			typeof candidate === "string" && candidate.trim().length > 0,
	);

	return {
		date,
		name: name || JSON.stringify(value),
	};
};

const normalizeHolidaysResponse = (value: unknown): Holiday[] => {
	if (typeof value === "string") {
		return parseHolidayString(value);
	}

	if (Array.isArray(value)) {
		return value
			.map((item) => {
				if (typeof item === "string") {
					return parseHolidayString(item);
				}
				if (item && typeof item === "object") {
					return [normalizeHolidayObject(item as Record<string, unknown>)];
				}
				return [];
			})
			.flat();
	}

	if (value && typeof value === "object") {
		const objectValue = value as Record<string, unknown>;
		if (Array.isArray(objectValue.holidays)) {
			return normalizeHolidaysResponse(objectValue.holidays);
		}

		return Object.entries(objectValue).map(([date, holiday]) => ({
			date,
			name: typeof holiday === "string" ? holiday : JSON.stringify(holiday),
		}));
	}

	return [];
};

/**
 * Get all monitors
 */
export const getScreens = async (
	from_date?: string,
	to_date?: string,
): Promise<Monitor[]> => {
	if (USE_MOCK_API) {
		return mockGetScreens(from_date, to_date);
	}

	const params: Record<string, string> = {};
	if (from_date && to_date) {
		params.from_date = from_date;
		params.to_date = to_date;
	}

	const response = await apiClient.get<Monitor[]>("/monitors", { params });
	return response.data;
};

/**
 * Get all media
 */
export const getMedia = async (): Promise<Media[]> => {
	if (USE_MOCK_API) {
		return mockGetMedia();
	}

	const response = await apiClient.get<Media[]>("/media");
	return response.data;
};

/**
 * Get holidays for a date range
 */
export const getHolidays = async (
	start_date: string,
	end_date: string,
): Promise<Holiday[]> => {
	if (USE_MOCK_API) {
		return mockGetHolidays(start_date, end_date);
	}

	const response = await apiClient.get<unknown>("/holidays", {
		params: { start_date, end_date },
	});
	return normalizeHolidaysResponse(response.data);
};

/**
 * Generate report
 */
export const generateReport = async (
	reportData: ReportRequest,
): Promise<Blob> => {
	if (USE_MOCK_API) {
		return mockGenerateReport(reportData);
	}

	const response = await apiClient.post("/reports", reportData, {
		responseType: "blob",
	});
	return response.data;
};

/**
 * Get all reports
 */
export const getAllReports = async (): Promise<Report[]> => {
	if (USE_MOCK_API) {
		return mockGetAllReports();
	}

	const response = await apiClient.get<ReportListResponse>("/reports");
	return response.data.reports;
};

/**
 * Download report by ID.
 * The real API redirects to a presigned Spaces URL, so we open it via
 * browser navigation to avoid CORS issues with the cross-origin redirect.
 */
export const downloadReport = async (reportId: string): Promise<void> => {
	if (USE_MOCK_API) {
		const blob = await mockDownloadReport(reportId);
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `report-${reportId}.pdf`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
		return;
	}

	const baseURL = (apiClient.defaults.baseURL || "").replace(/\/+$/, "");
	const link = document.createElement("a");
	link.href = `${baseURL}/reports/${reportId}/download`;
	link.style.display = "none";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};
